import { useState, useEffect } from "react";
import axios from "axios";
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PopupDisplay = ({ location }) => {
  const [popups, setPopups] = useState([]);
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [sessionReadPopups, setSessionReadPopups] = useState([]);
  const [temporarilyDismissed, setTemporarilyDismissed] = useState([]);

  useEffect(() => {
    fetchPopups();
    const sessionRead = JSON.parse(sessionStorage.getItem('sessionReadPopups') || '[]');
    setSessionReadPopups(sessionRead);
  }, [location]);

  useEffect(() => {
    const unreadPopups = getUnreadPopups();
    setIsOpen(unreadPopups.length > 0);
  }, [popups, sessionReadPopups, temporarilyDismissed]);

  const fetchPopups = async () => {
    try {
      const response = await axios.get(`${API}/popups/location/${location}`);
      let combinedPopups = response.data;
      
      try {
        const allResponse = await axios.get(`${API}/popups/location/all`);
        combinedPopups = [...response.data, ...allResponse.data];
      } catch (err) {
        console.log("No global popups");
      }
      
      const uniquePopups = combinedPopups.filter((popup, index, self) =>
        index === self.findIndex((p) => p.id === popup.id)
      );
      
      setPopups(uniquePopups);
    } catch (error) {
      console.error("Failed to fetch popups:", error);
    }
  };

  const getUnreadPopups = () => {
    return popups.filter(popup => 
      !sessionReadPopups.includes(popup.id) && 
      !temporarilyDismissed.includes(popup.id)
    );
  };

  const markAsRead = async (popupId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/popups/${popupId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Failed to mark popup as read:", error);
    }
    
    const sessionRead = [...sessionReadPopups, popupId];
    setSessionReadPopups(sessionRead);
    sessionStorage.setItem('sessionReadPopups', JSON.stringify(sessionRead));
    
    const remaining = getUnreadPopups().filter(p => p.id !== popupId);
    if (remaining.length > 0 && currentPopupIndex < remaining.length) {
      setCurrentPopupIndex(currentPopupIndex);
    } else if (remaining.length > 0) {
      setCurrentPopupIndex(0);
    } else {
      setIsOpen(false);
      setCurrentPopupIndex(0);
    }
  };

  const temporarilyDismiss = (popupId) => {
    const dismissed = [...temporarilyDismissed, popupId];
    setTemporarilyDismissed(dismissed);
    
    const remaining = getUnreadPopups().filter(p => p.id !== popupId);
    if (remaining.length > 0 && currentPopupIndex < remaining.length) {
      setCurrentPopupIndex(currentPopupIndex);
    } else if (remaining.length > 0) {
      setCurrentPopupIndex(0);
    } else {
      setIsOpen(false);
      setCurrentPopupIndex(0);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "success": return <CheckCircle className="h-6 w-6" />;
      case "warning": return <AlertTriangle className="h-6 w-6" />;
      case "error": return <AlertCircle className="h-6 w-6" />;
      default: return <Info className="h-6 w-6" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "success": return { bg: "#10B98120", border: "#10B981", text: "#10B981" };
      case "warning": return { bg: "#F59E0B20", border: "#F59E0B", text: "#F59E0B" };
      case "error": return { bg: "#EF444420", border: "#EF4444", text: "#EF4444" };
      default: return { bg: "#3B82F620", border: "#3B82F6", text: "#3B82F6" };
    }
  };

  const unreadPopups = getUnreadPopups();
  
  if (unreadPopups.length === 0 || !isOpen) return null;

  const currentPopup = unreadPopups[currentPopupIndex];
  if (!currentPopup) return null;

  const colors = getTypeColor(currentPopup.type);

  return (
    <div
      data-testid="popup-display-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 45,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div
        data-testid="popup-display-content"
        style={{
          pointerEvents: 'auto',
          background: colors.bg,
          border: `2px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          position: 'relative',
          backdropFilter: 'blur(8px)',
        }}
      >
        <button
          data-testid="popup-dismiss-btn"
          onClick={() => temporarilyDismiss(currentPopup.id)}
          style={{
            position: 'absolute',
            right: '16px',
            top: '16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: colors.text,
            opacity: 0.7,
            padding: '4px',
          }}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div style={{ color: colors.text }}>
            {getTypeIcon(currentPopup.type)}
          </div>
          <h3 style={{ color: colors.text, fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            {currentPopup.title}
          </h3>
        </div>

        <div>
          <p className="text-base whitespace-pre-wrap" style={{ color: '#E8E8E8', lineHeight: '1.6' }}>
            {currentPopup.message}
          </p>
          
          {(currentPopup.valid_from || currentPopup.valid_until) && (
            <div className="mt-3 p-2 rounded text-xs" style={{ background: '#1E2A4A', color: '#B0B0B0' }}>
              Valid: {currentPopup.valid_from || 'Now'} &rarr; {currentPopup.valid_until || 'Forever'}
            </div>
          )}
          
          {unreadPopups.length > 1 && (
            <p className="text-xs mt-3" style={{ color: '#B0B0B0' }}>
              {currentPopupIndex + 1} of {unreadPopups.length} notifications
            </p>
          )}
          
          <div className="flex justify-end mt-6">
            <Button
              data-testid="popup-read-btn"
              onClick={() => markAsRead(currentPopup.id)}
              style={{ 
                background: colors.text,
                color: '#FFFFFF',
                padding: '0.75rem 1.5rem',
                fontWeight: '600'
              }}
            >
              OK, I have read
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupDisplay;
